/* eslint-disable @typescript-eslint/no-shadow */

/* Be very careful of cyclic dependencies! */
import { useAgents } from '#~/agent/hooks.js';
import { locateProxy } from '#~/agent/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useVideoTrack, useVideoTrackWaitState } from '#~/livekit/hooks.js';
import { failVideoTrackWait, type VideoTrackWaitState } from '#~/livekit/track-wait.js';
import { Badge as VideoBadge } from '#~/video/components/badge/index.js';
import { BadgeStatus } from '#~/video/components/badge/types.js';
import { badgeStatusForVideoTrackWait } from '#~/video/components/badge/wait-status.js';
import type { Props } from './types.js';
import { Badge, Container } from '@studio/design-system/badge';
import { parseUri } from '@studio/models/net';
import { useLatestFn, useLazyRef, useUnmount } from '@studio/react-utils/hooks';
import type { StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  StatusCalibrationState,
  StatusDeviceConnectionStatus,
  StatusDeviceOverallStatus
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { DeviceRegistryCommandKey } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe, T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import type { Device } from '@studio/models/device';

const timeoutMap: Record<number, number> = {
  [BadgeStatus.BADGE_STATUS_CONNECTED]: 2000,
  [BadgeStatus.BADGE_STATUS_CALIBRATION_FAILED]: 10_000,
  [BadgeStatus.BADGE_STATUS_CALIBRATION_GENERIC]: Number.POSITIVE_INFINITY,
  [BadgeStatus.BADGE_STATUS_CALIBRATION_SUCCEEDED]: 2000,
  [BadgeStatus.BADGE_STATUS_BRIDGE_OFFLINE]: -1
};

// Agents report the source codec as this MIME type (newts MimeTypeH265).
const H265_MIME_TYPE = 'video/h265';

// Registry option name of a camera stream's encoding when it is set to H.265; models spell it
// "H265" or "H.265", so compare without case or dot.
const H265_ENCODING_OPTION = 'h265';

// Encoding setting of each stream a numbered RTSP path selects.
const NUMBERED_STREAM_ENCODING: Record<string, DeviceRegistryCommandKey> = {
  '1': DeviceRegistryCommandKey.STREAM_1_ENCODING,
  '2': DeviceRegistryCommandKey.STREAM_2_ENCODING,
  '3': DeviceRegistryCommandKey.STREAM_3_ENCODING
};

// The encoding setting of the stream the camera's agent plays, from the source's video URI. A
// numbered path names its stream (the PTZOptics Move SE plays "/2", the Link 4K "/1"). The other
// Iris-enabled models' registry paths carry no stream number ("/hevc", "/stream/main",
// "/live/av0") and are read as stream 1. Without a URI, or with a stream number that has no
// encoding setting, the stream is unknown.
// Per rule: absent-data-gates-permissively (ai.knowledge/global) — an unknown stream is not H.265.
const feedEncodingKey = (videoUri: T_Maybe<string>): T_Maybe<DeviceRegistryCommandKey> => {
  const path = parseUri(videoUri)?.path;

  if (path === undefined) {
    return undefined;
  }

  return /^\d+$/.test(path)
    ? NUMBERED_STREAM_ENCODING[path]
    : DeviceRegistryCommandKey.STREAM_1_ENCODING;
};

// The camera's own encoding for the given stream, named by the registry option the agent reports
// as selected.
const streamEncoding = (
  status: T_Maybe<StatusDevice>,
  key: T_Maybe<DeviceRegistryCommandKey>
): T_Maybe<string> => {
  if (key === undefined) {
    return undefined;
  }

  const settings = status?.deviceSettings;
  const selected = settings?.settings[key]?.select;

  return selected === undefined
    ? undefined
    : settings?.commandDefinitions[key]?.select?.options[selected]?.name;
};

// Whether the feed is H.265. The codec the agent reports is authoritative; older agents report
// none, so the camera's encoding setting for the stream it plays names the feed for them.
const isH265Feed = (status: T_Maybe<StatusDevice>, videoUri: T_Maybe<string>): boolean => {
  const codec = status?.video?.status?.codec;

  return codec
    ? codec.toLowerCase() === H265_MIME_TYPE
    : streamEncoding(status, feedEncodingKey(videoUri))?.toLowerCase().replace('.', '') ===
        H265_ENCODING_OPTION;
};

// Live H.265 is a supported input, so an Iris-enabled agent whose feed is H.265, that never
// streamed it and declared the connection failed, cannot stream H.265.
// See knowledge: h265-input-supported-output-not (ai.knowledge/platform) — the live-path loop.
const isEmbeddedH265Failure = (
  status: T_Maybe<StatusDevice>,
  embedded: T_Maybe<boolean>,
  videoUri: T_Maybe<string>
): boolean => {
  const video = status?.video;

  return (
    embedded === true &&
    isH265Feed(status, videoUri) &&
    // Per rule: absent-data-gates-permissively (ai.knowledge/global) — unreported is not "never streamed".
    video?.status?.hasConnected === false &&
    // Only the agent's own verdict counts: it already absorbs first-connect grace and retries.
    (status?.overallStatus?.status === StatusDeviceOverallStatus.OVERALL_STATUS_CONNECTION_FAILED ||
      video.connectionStatus?.status === StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED)
  );
};

const resolveBadgeStatusForDevice = (
  status: T_Maybe<StatusDevice>,
  isTrackConnected: boolean,
  agentStatus?: string,
  videoWaitState?: T_Maybe<VideoTrackWaitState>
): BadgeStatus => {
  // Check if the agent is offline first
  if (agentStatus === 'offline') {
    return BadgeStatus.BADGE_STATUS_BRIDGE_OFFLINE;
  }

  const directOverallStatusMappings = {
    [StatusDeviceOverallStatus.OVERALL_STATUS_SLEEPING]: BadgeStatus.BADGE_STATUS_IDLE,
    [StatusDeviceOverallStatus.OVERALL_STATUS_WAKING]: BadgeStatus.BADGE_STATUS_BOOTING,
    [StatusDeviceOverallStatus.OVERALL_STATUS_REBOOTING]: BadgeStatus.BADGE_STATUS_REBOOTING,
    [StatusDeviceOverallStatus.OVERALL_STATUS_SHUTTING_DOWN]: BadgeStatus.BADGE_STATUS_SHUTTING_DOWN
  };

  const overallStatus = status?.overallStatus?.status;

  if (overallStatus && overallStatus in directOverallStatusMappings) {
    // @ts-expect-error
    return directOverallStatusMappings[overallStatus] as BadgeStatus;
  }

  // Bridge connection state does not prove that the viewer received video.
  // Keep offline and power-transition precedence, but show a failed track wait before bridge-only
  // Connecting/Connected (or generic failure) can mask its actionable cause.
  if (!isTrackConnected && videoWaitState?.phase === 'failed') {
    return badgeStatusForVideoTrackWait(videoWaitState);
  }

  if (overallStatus === StatusDeviceOverallStatus.OVERALL_STATUS_CONNECTING) {
    return BadgeStatus.BADGE_STATUS_CONNECTING;
  }

  const hasVideo = !!status?.video?.connectionStatus?.status;
  const hasAudio = !!status?.audio?.connectionStatus?.status;
  const hasMovement = !!status?.movement?.connectionStatus?.status;

  const candidateBucket: string[] = [];
  // const connectingBucket: string[] = [];
  const connectedBucket: string[] = [];
  const failedBucket: string[] = [];

  // const isVideoConnecting =
  //   status?.video?.connectionStatus?.status ===
  //   StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTING;

  const isVideoConnected =
    status?.video?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

  const hasVideoConnectionFailed =
    status?.video?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

  // const isAudioConnecting =
  //   status?.audio?.connectionStatus?.status ===
  //   StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTING;

  const isAudioConnected =
    status?.audio?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

  const hasAudioConnectionFailed =
    status?.audio?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

  // const isMovementConnecting =
  //   status?.movement?.connectionStatus?.status ===
  //   StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTING;

  const isMovementConnected =
    status?.movement?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

  const hasMovementConnectionFailed =
    status?.movement?.connectionStatus?.status ===
    StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

  if (hasVideo) {
    candidateBucket.push('video');

    // if (isVideoConnecting) {
    //   connectingBucket.push('video');
    // }

    if (isVideoConnected) {
      connectedBucket.push('video');
    }

    if (hasVideoConnectionFailed) {
      failedBucket.push('video');
    }
  }

  if (hasAudio) {
    candidateBucket.push('audio');

    // if (isAudioConnecting) {
    //   connectingBucket.push('audio');
    // }

    if (isAudioConnected) {
      connectedBucket.push('audio');
    }

    if (hasAudioConnectionFailed) {
      failedBucket.push('audio');
    }
  }

  // Ignore movement status when video is connected.
  if (hasMovement && !isVideoConnected) {
    candidateBucket.push('movement');

    // if (isMovementConnecting) {
    //   connectingBucket.push('movement');
    // }

    if (isMovementConnected) {
      connectedBucket.push('movement');
    }

    if (hasMovementConnectionFailed) {
      failedBucket.push('movement');
    }
  }

  if (candidateBucket.length === 1) {
    const [deviceType] = candidateBucket;

    if (deviceType === 'video') {
      // if (isVideoConnecting) {
      //   return BadgeStatus.BADGE_STATUS_VIDEO_CONNECTING;
      // }

      if (hasVideoConnectionFailed) {
        return BadgeStatus.BADGE_STATUS_VIDEO_CONNECTION_FAILED;
      }
    }

    if (deviceType === 'audio') {
      // if (isAudioConnecting) {
      //   return BadgeStatus.BADGE_STATUS_AUDIO_CONNECTING;
      // }

      if (hasAudioConnectionFailed) {
        return BadgeStatus.BADGE_STATUS_AUDIO_CONNECTION_FAILED;
      }
    }

    if (deviceType === 'movement') {
      // if (isMovementConnecting) {
      //   return BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTING;
      // }

      if (hasMovementConnectionFailed) {
        return BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTION_FAILED;
      }
    }
  }

  // const allConnecting =
  //   candidateBucket.length > 0 && candidateBucket.length === connectingBucket.length;

  const allConnected =
    candidateBucket.length > 0 && candidateBucket.length === connectedBucket.length;

  // const allConnectionFailed =
  //   candidateBucket.length > 0 && candidateBucket.length === failedBucket.length;

  // if (allConnecting) {
  //   return BadgeStatus.BADGE_STATUS_CONNECTING;
  // }

  if (failedBucket.length > 0) {
    const [deviceType] = failedBucket;

    if (deviceType === 'video') {
      return BadgeStatus.BADGE_STATUS_VIDEO_CONNECTION_FAILED;
    }

    if (deviceType === 'audio') {
      return BadgeStatus.BADGE_STATUS_AUDIO_CONNECTION_FAILED;
    }

    if (deviceType === 'movement') {
      return BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTION_FAILED;
    }
  }

  // if (connectingBucket.length > 0) {
  //   const [deviceType] = connectingBucket;

  //   if (deviceType === 'video') {
  //     return BadgeStatus.BADGE_STATUS_VIDEO_CONNECTING;
  //   }

  //   if (deviceType === 'audio') {
  //     return BadgeStatus.BADGE_STATUS_AUDIO_CONNECTING;
  //   }

  //   if (deviceType === 'movement') {
  //     return BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTING;
  //   }
  // }

  const isCalibrating =
    status?.calibration?.status?.state === StatusCalibrationState.CALIBRATION_STATE_RUNNING;

  if (isCalibrating) {
    return BadgeStatus.BADGE_STATUS_CALIBRATING;
  }

  const hasCalibrationFailed =
    status?.calibration?.status?.state === StatusCalibrationState.CALIBRATION_STATE_FAIL;

  if (hasCalibrationFailed) {
    return BadgeStatus.BADGE_STATUS_CALIBRATION_FAILED;
  }

  const hasCalibraitonSucceeded =
    status?.calibration?.status?.state === StatusCalibrationState.CALIBRATION_STATE_SUCCESS;

  const hasGenericCalibration =
    status?.calibration?.status?.state === StatusCalibrationState.CALIBRATION_STATE_OK_GENERIC;

  if (hasGenericCalibration) {
    return BadgeStatus.BADGE_STATUS_CALIBRATION_GENERIC;
  }

  if (hasCalibraitonSucceeded) {
    return BadgeStatus.BADGE_STATUS_CALIBRATION_SUCCEEDED;
  }

  if (allConnected) {
    return BadgeStatus.BADGE_STATUS_CONNECTED;
  }

  // ENG-3716: the old fallback pinned the badge on "Connecting..." forever when
  // the video track never subscribed. The waiting-for-video-track state machine
  // (livekit/track-wait.ts) now supplies a concrete, cause-keyed failure state
  // once the ~15s wait expires (or a snapshot-restore validation fails); a
  // later successful subscribe still recovers to CONNECTED via isTrackConnected.
  return isTrackConnected
    ? BadgeStatus.BADGE_STATUS_CONNECTED
    : badgeStatusForVideoTrackWait(videoWaitState);
};

const lastKnownBadgeMap: Record<
  Device['id'],
  {
    status: BadgeStatus;
    shownAt: number;
    timeout: number | undefined;
  }
> = {};

/** `<Status>` component. */
export const Status = ({ className, deviceId, status, ...props }: Props): ReactElement => {
  // const [audioStatus, setAudioStatus] = useDelayedState<M_Status['audio']>(undefined);
  // const [controlStatus, setControlStatus] = useDelayedState<M_Status['control']>(undefined);
  // const [deviceStatus, setDeviceStatus] = useDelayedState<M_Status['device']>(undefined);
  // const [videoStatus, setVideoStatus] = useDelayedState<M_Status['video']>(undefined);
  const recovery = useDepot(
    (state) =>
      Object.values(state.rooms).find((room) => room.devices[deviceId])?.devices[deviceId]
        ?.sourceRecovery
  );
  const track = useVideoTrack(deviceId);
  // ENG-3716: waiting-for-video-track state machine — drives the cause-keyed
  // failure badges when the expected subscribe never arrives.
  const videoWaitState = useVideoTrackWaitState(deviceId);
  const proxy = locateProxy(deviceId);
  const statusDevice = proxy?.state.status?.device[deviceId];

  // Keep track of whether this is the first time we're setting a status.
  const firstTime = useLazyRef(() => ({
    audio: true,
    control: true,
    device: true,
    video: true
  }));

  useUnmount(() => {
    firstTime.current = {
      audio: true,
      control: true,
      device: true,
      video: true
    };
  });

  /*
  // Watch the audio status
  useDeepEffect(() => {
    const next = status.audio;
    const final = isStreamConnected(next?.status);

    // Is this the first time we're setting the audio status?
    const b = χref(firstTime).audio;

    firstTime.current.audio = false;

    // If the audio stream is initially in its final state, we don't need to display a badge.
    if (b && final) {
      setAudioStatus(undefined);

      return;
    }

    // Update the audio status.
    setAudioStatus(next);

    // Once the audio status reaches its final state, only display it for short interval.
    if (final) {
      setAudioStatus(undefined, Timeout);
    }
  }, [status.audio]);
*/
  /*
  // Watch the control status
  useDeepEffect(() => {
    const next = status.control;

    const final =
      isMovementConnected(next?.movement?.status) && !isCalibrating(next?.calibration?.status);

    // Is this the first time we're setting the control status?
    const b = χref(firstTime).control;

    firstTime.current.control = false;

    // If the control status is initially in its final state, we don't need to display a badge.
    if (b && final) {
      setControlStatus(undefined);

      return;
    }

    // Update the control status.
    setControlStatus(next);

    // Once the control status reaches its final state, only display it for short interval.
    if (final) {
      setControlStatus(undefined, Timeout);
    }
  }, [status.control]);
*/
  /*
  // Watch the device status
  useDeepEffect(() => {
    if(props.hideDeviceStatus) {
      return;
    }

    const next = status.device;
    const final = next?.powerState === StatusPowerState.POWER_STATE_ON;
    const unknown = next?.powerState === StatusPowerState.POWER_STATE_UNKNOWN;

    // Is this the first time we're setting the device status?
    const b = χref(firstTime).device;

    firstTime.current.device = false;

    // If the device status is initially in its final state, or its status isn't known, we don't need to display a badge.
    if ((b && final) || unknown) {
      setDeviceStatus(undefined);

      return;
    }

    // Update the device status.
    setDeviceStatus(next);

    // Once the device status reaches its final state, only display it for short interval.
    if (final) {
      setDeviceStatus(undefined, Timeout);
    }
  }, [status.device]);
*/
  /*
  // Watch the video status
  useDeepEffect(() => {
    const next = {
      network: 0,
      status: StatusStreamState.STREAM_STATE_UNKNOWN,
      ...status.video
    };

    if(!track && next.status !== StatusStreamState.STREAM_STATE_FAIL) {
      next.status = StatusStreamState.STREAM_STATE_UNKNOWN;
    }

    const final =
      isStreamConnected(next.status) &&
      (next.network === undefined || toQuality(next.network) === 'good');

    // Is this the first time we're setting the video status?
    const b = χref(firstTime).video;

    firstTime.current.video = false;

    // If the video status is initially in its final state, we don't need to display a badge.
    if (b && final) {
      setVideoStatus(undefined, Timeout);

      return;
    }

    // Update the video status.
    setVideoStatus(next);

    // Once the video status reaches its final state, only display it for short interval.
    if (final) {
      setVideoStatus(undefined, Timeout);
    }
  }, [status.video, track]);
*/
  // Introduce a delay before we render the badge.
  const [badge, setBadge] = useState<T_Nullable<ReactElement>>(null);
  const [calibrationProgress, setCalibrationProgress] = useState<number | undefined>(undefined);
  const [forceRefreshTimestamp, setForceRefreshTimestamp] = useState<number>(0);

  const [badgeStatus, setBadgeStatus] = useState<
    T_Nullable<{
      status: BadgeStatus;
      shownAt: number;
      timeout: number | undefined;
    }>
  >(null);

  const agentId = locateProxy(deviceId)?.id;
  const agent = useAgents((a) => a.find((a) => a.id === agentId));

  const { calibration } = useDepot((µ) => {
    if (!agent?.roomId) {
      return {};
    }

    const calibration = µ.rooms[agent.roomId]?.devices[deviceId]?.control?.calibration;

    return {
      calibration
    };
  }, shallow);

  const handleSync = useLatestFn(() => {
    // The agent's retry status alternates Connecting and Failed every few seconds. Latch its
    // H.265 failure as a terminal wait cause so the tile holds it until a track or retry replaces it.
    if (
      !track &&
      // Only a live agent's verdict counts; embedded agents go 'offline-embedded', not 'offline'.
      agent?.status === 'online' &&
      // A connect attempt owns its wait window: until it ends, the status may be the last attempt's.
      videoWaitState?.phase !== 'waiting' &&
      isEmbeddedH265Failure(
        statusDevice as T_Maybe<StatusDevice>,
        proxy?.embedded,
        proxy?.state.control?.studio?.device?.[deviceId]?.video?.uri
      ) &&
      !(videoWaitState?.phase === 'failed' && videoWaitState.cause === 'h265-unsupported')
    ) {
      // The store notification re-runs this sync against the failed state.
      failVideoTrackWait(deviceId, 'h265-unsupported');
      return;
    }

    let nextBadge: T_Nullable<ReactElement> = null;

    const nextBadgeStatus = resolveBadgeStatusForDevice(
      statusDevice as T_Maybe<StatusDevice>,
      !!track,
      agent?.status,
      videoWaitState
    );

    const nextCalibrationProgress = calibration?.progress;

    const lastKnownBadgeStatus = lastKnownBadgeMap[deviceId];

    if (
      lastKnownBadgeStatus?.status !== undefined &&
      nextBadgeStatus === lastKnownBadgeStatus?.status &&
      (lastKnownBadgeStatus?.timeout ?? 0) !== 0 &&
      Date.now() - lastKnownBadgeStatus!.shownAt > lastKnownBadgeStatus!.timeout!
    ) {
      setBadge(null);
      return;
    }

    if (
      nextBadgeStatus !== badgeStatus?.status ||
      (nextBadgeStatus === BadgeStatus.BADGE_STATUS_CALIBRATING &&
        nextCalibrationProgress !== calibrationProgress)
    ) {
      nextBadge = (
        <VideoBadge
          badgeStatus={nextBadgeStatus}
          calibrationProgress={nextCalibrationProgress}
          deviceId={deviceId}
          wrap
        />
      );

      const status = {
        status: nextBadgeStatus,
        shownAt: Date.now(),
        timeout: timeoutMap[nextBadgeStatus] ?? undefined
      };

      setBadgeStatus(status);

      setBadge(nextBadge);
      setCalibrationProgress(nextCalibrationProgress);

      if (lastKnownBadgeMap[deviceId]?.status !== nextBadgeStatus) {
        lastKnownBadgeMap[deviceId] = status;
      }

      const timeoutDuration = timeoutMap[nextBadgeStatus] ?? 0;

      if (timeoutDuration !== 0) {
        setTimeout(() => {
          setForceRefreshTimestamp(Date.now());
        }, timeoutDuration);
      }
    }
  });

  useEffect(
    () => {
      handleSync();
    },
    // eslint-disable-next-line react-hooks-addons/no-unused-deps
    [
      agent,
      status,
      track,
      // ENG-3716: recompute the badge when the video-track wait times out/fails.
      videoWaitState,
      calibration,
      calibration?.progress,
      forceRefreshTimestamp
    ]
  );

  return (
    <Container
      {...props}
      // Cap the pill at its host's width (8px inset per side) so long copy wraps inside
      // narrow hosts like source-tray tiles instead of running past both edges.
      className={twMerge('w-max max-w-[calc(100%-16px)]', className)}
    >
      {/* Only the two restore states replace the badge; any other value (absent row) falls through. */}
      {recovery === 'recovering' || recovery === 'unavailable' ? (
        // Restore states are first-class status badges (spec §01): Recovering is
        // in-progress -> the design-system's neutral/default `info` tone; Unavailable
        // needs attention -> `warning`. The width cap stays on the Container above.
        <Badge
          message={recovery === 'recovering' ? 'Recovering' : 'Unavailable'}
          severity={recovery === 'recovering' ? 'info' : 'warning'}
        />
      ) : (
        badge
      )}
    </Container>
  );
};
